import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  IndianRupee,
  Activity,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Truck,
  CheckCircle,
  XCircle
} from "lucide-react";

export default function AdminDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state for add/edit project
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    name_hindi: '',
    category: '',
    total_cost: '',
    description: '',
    description_hindi: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes, projectsRes, logsRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/projects?limit=100`),
        axios.get(`${API}/admin/activity-logs`, { headers }),
        axios.get(`${API}/admin/suppliers`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
      setActivityLogs(logsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error("डेटा लोड करने में त्रुटि");
    }
    setLoading(false);
  };

  const handleVerifySupplier = async (supplierId, verify = true) => {
    try {
      const endpoint = verify ? "verify" : "unverify";
      await axios.put(
        `${API}/admin/suppliers/${supplierId}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(verify ? "सप्लायर verified" : "सप्लायर unverified");
      fetchData();
    } catch (error) {
      toast.error("त्रुटि हुई");
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm("क्या आप इस सप्लायर को डिलीट करना चाहते हैं?")) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("सप्लायर डिलीट हो गया");
      fetchData();
    } catch (error) {
      toast.error("डिलीट करने में त्रुटि");
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...projectForm,
        total_cost: parseInt(projectForm.total_cost)
      };

      if (editingProject) {
        await axios.put(`${API}/admin/projects/${editingProject.id}`, payload, { headers });
        toast.success("प्रोजेक्ट अपडेट हो गया");
      } else {
        await axios.post(`${API}/admin/projects`, payload, { headers });
        toast.success("नया प्रोजेक्ट जोड़ा गया");
      }

      setProjectDialogOpen(false);
      setEditingProject(null);
      setProjectForm({ name: '', name_hindi: '', category: '', total_cost: '', description: '', description_hindi: '' });
      fetchData();
    } catch (error) {
      toast.error("कुछ गलत हो गया");
    }
    setFormLoading(false);
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("क्या आप इस प्रोजेक्ट को डिलीट करना चाहते हैं?")) return;

    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("प्रोजेक्ट डिलीट हो गया");
      fetchData();
    } catch (error) {
      toast.error("डिलीट करने में त्रुटि");
    }
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      name_hindi: project.name_hindi,
      category: project.category,
      total_cost: project.total_cost.toString(),
      description: project.description || '',
      description_hindi: project.description_hindi || ''
    });
    setProjectDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('hi-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-32 bg-white mb-4" />
          <Skeleton className="h-64 bg-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
            <LayoutDashboard className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <h1 className="font-rajdhani font-bold text-2xl text-gray-800">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-8000">प्रोजेक्ट्स और यूजर्स मैनेज करें</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <Users className="w-6 h-6 text-blue-500 mb-2" />
              <p className="font-rajdhani font-bold text-2xl text-gray-800">{stats.total_users}</p>
              <p className="text-xs text-gray-8000">कुल यूजर्स</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <Briefcase className="w-6 h-6 text-emerald-500 mb-2" />
              <p className="font-rajdhani font-bold text-2xl text-gray-800">{stats.total_projects}</p>
              <p className="text-xs text-gray-8000">कुल प्रोजेक्ट्स</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <Activity className="w-6 h-6 text-purple-500 mb-2" />
              <p className="font-rajdhani font-bold text-2xl text-gray-800">{stats.total_purchases}</p>
              <p className="text-xs text-gray-8000">कुल खरीदारी</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <IndianRupee className="w-6 h-6 text-orange-500 mb-2" />
              <p className="font-rajdhani font-bold text-2xl text-gray-800">₹{stats.total_revenue?.toLocaleString()}</p>
              <p className="text-xs text-gray-8000">कुल रेवेन्यू</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="projects" className="space-y-4">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="projects" className="data-[state=active]:bg-orange-500">
              प्रोजेक्ट्स
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="data-[state=active]:bg-orange-500">
              सप्लायर्स
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-orange-500">
              यूजर्स
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-orange-500">
              एक्टिविटी
            </TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-rajdhani font-bold text-lg">सभी प्रोजेक्ट्स</h3>
                <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => {
                        setEditingProject(null);
                        setProjectForm({ name: '', name_hindi: '', category: '', total_cost: '', description: '', description_hindi: '' });
                      }}
                      className="btn-primary rounded-sm"
                      data-testid="add-project-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      नया जोड़ें
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-gray-200">
                    <DialogHeader>
                      <DialogTitle className="font-rajdhani">
                        {editingProject ? 'प्रोजेक्ट एडिट करें' : 'नया प्रोजेक्ट जोड़ें'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleProjectSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>नाम (English)</Label>
                          <Input
                            value={projectForm.name}
                            onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                            className="input-industrial mt-1"
                            required
                          />
                        </div>
                        <div>
                          <Label>नाम (हिंदी)</Label>
                          <Input
                            value={projectForm.name_hindi}
                            onChange={(e) => setProjectForm({ ...projectForm, name_hindi: e.target.value })}
                            className="input-industrial mt-1"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>श्रेणी</Label>
                          <Input
                            value={projectForm.category}
                            onChange={(e) => setProjectForm({ ...projectForm, category: e.target.value })}
                            className="input-industrial mt-1"
                            placeholder="AGRO BASED"
                            required
                          />
                        </div>
                        <div>
                          <Label>कुल लागत (₹)</Label>
                          <Input
                            type="number"
                            value={projectForm.total_cost}
                            onChange={(e) => setProjectForm({ ...projectForm, total_cost: e.target.value })}
                            className="input-industrial mt-1"
                            required
                          />
                        </div>
                      </div>
                      <Button type="submit" disabled={formLoading} className="w-full btn-primary rounded-sm">
                        {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {editingProject ? 'अपडेट करें' : 'जोड़ें'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="overflow-x-auto">
                <Table className="table-industrial">
                  <TableHeader>
                    <TableRow>
                      <TableHead>नाम</TableHead>
                      <TableHead>श्रेणी</TableHead>
                      <TableHead>लागत</TableHead>
                      <TableHead>एक्शन</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.slice(0, 20).map((project) => (
                      <TableRow key={project.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-700">{project.name_hindi}</p>
                            <p className="text-xs text-gray-8000">{project.name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-500">{project.category}</TableCell>
                        <TableCell className="text-orange-500 font-tabular-nums">
                          ₹{project.total_cost?.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditDialog(project)}
                              className="p-2 hover:bg-gray-100 rounded"
                            >
                              <Pencil className="w-4 h-4 text-gray-500" />
                            </button>
                            <button
                              onClick={() => handleDeleteProject(project.id)}
                              className="p-2 hover:bg-gray-100 rounded"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value="suppliers">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-rajdhani font-bold text-lg">सभी सप्लायर्स ({suppliers.length})</h3>
                <Button
                  onClick={() => navigate("/supplier-registration")}
                  className="btn-primary rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  नया सप्लायर
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table className="table-industrial">
                  <TableHeader>
                    <TableRow>
                      <TableHead>व्यवसाय</TableHead>
                      <TableHead>टाइप</TableHead>
                      <TableHead>शहर</TableHead>
                      <TableHead>श्रेणियां</TableHead>
                      <TableHead>स्टेटस</TableHead>
                      <TableHead>एक्शन</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.map((supplier) => (
                      <TableRow key={supplier.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-700">{supplier.business_name_hindi || supplier.business_name}</p>
                            <p className="text-xs text-gray-8000">{supplier.owner_name} | {supplier.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            supplier.supplier_type === "Machinery" 
                              ? "bg-blue-500/20 text-blue-400" 
                              : supplier.supplier_type === "Raw Material"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : "bg-purple-500/20 text-purple-400"
                          }>
                            {supplier.supplier_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500">{supplier.city}, {supplier.state}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {supplier.categories?.slice(0, 2).map((cat) => (
                              <Badge key={cat} className="bg-gray-100 text-gray-500 text-xs">
                                {cat.substring(0, 10)}...
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {supplier.is_verified ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              <CheckCircle className="w-3 h-3 mr-1" /> Verified
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-500/20 text-yellow-400">
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {!supplier.is_verified ? (
                              <button
                                onClick={() => handleVerifySupplier(supplier.id, true)}
                                className="p-2 hover:bg-emerald-500/20 rounded text-emerald-500"
                                title="Verify"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleVerifySupplier(supplier.id, false)}
                                className="p-2 hover:bg-yellow-500/20 rounded text-yellow-500"
                                title="Unverify"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteSupplier(supplier.id)}
                              className="p-2 hover:bg-gray-100 rounded"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-rajdhani font-bold text-lg mb-4">सभी यूजर्स</h3>
              <div className="overflow-x-auto">
                <Table className="table-industrial">
                  <TableHeader>
                    <TableRow>
                      <TableHead>नाम</TableHead>
                      <TableHead>ईमेल</TableHead>
                      <TableHead>फ़ोन</TableHead>
                      <TableHead>खरीदारी</TableHead>
                      <TableHead>रजिस्ट्रेशन</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="text-gray-700">{user.name}</TableCell>
                        <TableCell className="text-gray-500">{user.email}</TableCell>
                        <TableCell className="text-gray-500">{user.phone}</TableCell>
                        <TableCell className="text-orange-500">
                          {user.purchased_projects?.length || 0}
                        </TableCell>
                        <TableCell className="text-gray-8000">
                          {formatDate(user.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-rajdhani font-bold text-lg mb-4">हाल की एक्टिविटी</h3>
              <div className="space-y-3">
                {activityLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="text-gray-700">
                        <span className="font-medium">{log.user_name}</span>
                        <span className="text-gray-8000 mx-2">•</span>
                        <span className={`${
                          log.action === 'purchase' ? 'text-emerald-500' :
                          log.action === 'generate_dpr' ? 'text-blue-500' :
                          'text-gray-500'
                        }`}>
                          {log.action === 'purchase' ? 'खरीदा' :
                           log.action === 'generate_dpr' ? 'DPR बनाया' :
                           log.action === 'view_dpr' ? 'DPR देखा' : log.action}
                        </span>
                      </p>
                      <p className="text-sm text-gray-8000">{log.project_name}</p>
                    </div>
                    <div className="text-right">
                      {log.amount && (
                        <p className="text-orange-500 font-bold">₹{log.amount}</p>
                      )}
                      <p className="text-xs text-slate-600">{formatDate(log.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
